import React, {useCallback, useMemo, useRef} from 'react';
import {SidePopup} from '@tickchak/react-share/app';
import * as css from './AIMessageGenerator.css';
import {AIMessageGeneratorProps} from './utils/types';
import {useAIMessageGenerator} from './utils/useAIMessageGenerator';
import AIMessageGeneratorPanel from './AIMessageGeneratorPanel';
import InnerPop from '../newPopup/innerPop/InnerPop';
import useMobile from '../../hooks/useMobile';
import {logGoogleEvent} from '../../utils/analytics';

function AIMessageGenerator({
    show,
    title,
    placeholder,
    submit,
    approve,
    errorMessage,
    suggestions,
    analytics,
    container = 'sidePopup',
}: AIMessageGeneratorProps) {
    const {
        messages,
        promptText,
        setPromptText,
        draftLoading,
        approveLoading,
        error,
        reset,
        submitPrompt,
        handleApprove,
    } = useAIMessageGenerator({errorMessage, submit, approve, onClose: show.close, analytics});

    const isMobile = useMobile();
    const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
    const scrollColumnRef = useRef<HTMLDivElement>(null);

    const logAnalytics = useCallback((eventName: string, extra?: Record<string, unknown>) => {
        if(!analytics) {
            return;
        }
        logGoogleEvent(eventName, analytics.contentId, analytics.sourceTypeId, {
            popup_name: 'aiMessageGenerator',
            ...(analytics.context ?? {}),
            ...(extra ?? {}),
        });
    }, [analytics]);

    const handleSubmitPrompt = useCallback(async (promptOverride?: string) => {
        const text = (promptOverride ?? promptText).trim();
        if(!text || draftLoading) {
            return;
        }
        logAnalytics('ai_message_generator_submit_click', {button_name: 'submit'});
        await submitPrompt(text);
        requestAnimationFrame(() => {
            const scrollEl = scrollColumnRef.current;
            if(!scrollEl) {
                return;
            }
            scrollEl.scrollTo({top: scrollEl.scrollHeight, behavior: 'smooth'});
        });
    }, [promptText, draftLoading, submitPrompt, logAnalytics]);

    const handleSuggestionClick = useCallback((text: string, index: number) => {
        logAnalytics('ai_message_generator_suggestion_click', {
            suggestion_index: index,
            button_name: `suggestion_${index}`,
        });
        setPromptText(text);
        void handleSubmitPrompt(text);
    }, [logAnalytics, setPromptText, handleSubmitPrompt]);

    const resizePromptTextarea = useCallback((textarea?: HTMLTextAreaElement | null) => {
        const el = textarea ?? promptTextareaRef.current;
        if(!el) {
            return;
        }
        const maxHeight = css.PROMPT_TEXTAREA_MAX_HEIGHT_PX;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, []);

    const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        resizePromptTextarea(e.target);
        setPromptText(e.target.value);
    };

    const handlePromptKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if(e.key !== 'Enter' || e.shiftKey) {
            return;
        }
        e.preventDefault();
        if(!promptText.trim() || draftLoading) {
            return;
        }
        void handleSubmitPrompt();
    }, [promptText, draftLoading, handleSubmitPrompt]);

    const closePopup = useCallback(() => {
        show.close().then(() => reset());
    }, [show, reset]);

    const wrappedShow = useMemo(() => ({
        ...show,
        close: closePopup,
    }), [show, closePopup]);

    const panelProps = {
        messages,
        suggestions,
        promptText,
        placeholder,
        draftLoading,
        approveLoading,
        approveLabel: approve.label,
        submitLabel: submit.label,
        submitXhrState: submit.xhrState,
        error,
        isMobile,
        disableSubmit: !promptText.trim() || draftLoading,
        scrollColumnRef,
        promptTextareaRef,
        onSuggestionClick: handleSuggestionClick,
        onApprove: handleApprove,
        onPromptChange: handlePromptChange,
        onPromptKeyDown: handlePromptKeyDown,
        onSubmitPrompt: () => void handleSubmitPrompt(),
    };

    if(container === 'innerPop') {
        return (
            <>
                <css.panelGradientStyles />
                <css.innerPopWrapper
                    onClick={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                >
                    <InnerPop show={wrappedShow} title="" dataCy="ai-message-generator" buttons={{}}>
                        <SidePopup.Header
                            title={title}
                            onClose={closePopup}
                            className="ai-message-generator-header"
                            data-cy="ai-message-generator-header"
                        />
                        <AIMessageGeneratorPanel {...panelProps} innerPop />
                    </InnerPop>
                </css.innerPopWrapper>
            </>
        );
    }

    return (
        <>
            <css.panelGradientStyles />
            <SidePopup
                show={show}
                panelWidth={css.AI_PANEL_WIDTH}
                ariaLabel={title}
                className="ai-message-generator-panel"
                data-cy="ai-message-generator"
            >
                <SidePopup.Header
                    title={title}
                    onClose={closePopup}
                    className="ai-message-generator-header"
                    data-cy="ai-message-generator-header"
                />
                <AIMessageGeneratorPanel {...panelProps} />
            </SidePopup>
        </>
    );
}

export default AIMessageGenerator;
